import { tokens } from '@sunrun/experience-ui-components';

type LeaderboardQuartile = 'top' | 'middleTop' | 'middleBottom' | 'bottom';

const ROW_BASE = '#F6F3EA';
const ROW_TINT = 'rgb(226 226 226)';

const ZEBRA_EVEN_OVERLAY = 'rgba(31, 38, 71, 0.04)';
const ZEBRA_ODD_OVERLAY = 'rgba(31, 38, 71, 0.02)';

const QUARTILE_HIGHLIGHT_BORDERS: Record<LeaderboardQuartile, string> = {
  top: '#23A387',
  middleTop: '#23A387',
  middleBottom: '#A49825',
  bottom: '#9E1E19',
};

const QUARTILE_OVERLAYS: Record<LeaderboardQuartile, string> = {
  top: 'rgba(35, 163, 135, 0.5)',
  middleTop: 'rgba(35, 163, 135, 0.15)',
  middleBottom: 'rgba(254, 196, 59, 0.15)',
  bottom: 'rgba(255, 83, 103, 0.14)',
};

const solidLayer = (color: string): string =>
  `linear-gradient(${color}, ${color})`;

const layerQuartileWithZebra = (
  quartileOverlay: string,
  isEven: boolean,
): string => {
  const zebraOverlay = isEven ? ZEBRA_EVEN_OVERLAY : ZEBRA_ODD_OVERLAY;
  return `${solidLayer(zebraOverlay)}, ${solidLayer(
    quartileOverlay,
  )}, ${ROW_BASE}`;
};

type LeaderboardSortDirection = 'asc' | 'desc';

const getLeaderboardQuartile = (
  index: number,
  total: number,
  sortDirection: LeaderboardSortDirection = 'desc',
): LeaderboardQuartile | null => {
  if (total < 4) {
    return null;
  }

  const rankedIndex = sortDirection === 'asc' ? total - 1 - index : index;
  const quartileIndex = Math.floor((rankedIndex * 4) / total);

  switch (quartileIndex) {
    case 0:
      return 'top';
    case 1:
      return 'middleTop';
    case 2:
      return 'middleBottom';
    default:
      return 'bottom';
  }
};

type RowBackgroundArgs = {
  isEven: boolean;
  quartile: LeaderboardQuartile | null;
};

const getLeaderboardRowBackgroundColor = ({
  isEven,
  quartile,
}: RowBackgroundArgs): string => {
  if (quartile) {
    return layerQuartileWithZebra(QUARTILE_OVERLAYS[quartile], isEven);
  }

  return isEven ? ROW_TINT : ROW_BASE;
};

const getLeaderboardRowHighlightBorderColor = (
  quartile: LeaderboardQuartile | null,
): string => {
  if (!quartile) {
    return tokens.BRAND_MEDIUM_BLUE;
  }

  return QUARTILE_HIGHLIGHT_BORDERS[quartile];
};

export {
  getLeaderboardQuartile,
  getLeaderboardRowBackgroundColor,
  getLeaderboardRowHighlightBorderColor,
};
export type { LeaderboardQuartile, LeaderboardSortDirection };
